import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Search, Loader2 } from 'lucide-react';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const categorias = ['Aluguel', 'Salarios', 'Equipamentos', 'Manutencao', 'Marketing', 'Agua/Luz', 'Internet', 'Impostos', 'Outros'];
const initialForm = { descricao: '', categoria: 'Outros', valor: '', data: '', observacoes: '' };

export default function DespesasPage() {
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => { api.get('/gestor/despesas').then(r => setDespesas(r.data)).catch(() => toast.error('Erro')).finally(() => setLoading(false)); }, []);

  const refresh = async () => { const r = await api.get('/gestor/despesas'); setDespesas(r.data); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, valor: parseFloat(form.valor) };
      if (editing) { await api.put(`/gestor/despesas/${editing.id}`, payload); toast.success('Despesa atualizada'); }
      else { await api.post('/gestor/despesas', payload); toast.success('Despesa registrada'); }
      setDialogOpen(false); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Despesa', 'Tem certeza que deseja remover esta despesa?')) return;
    try { await api.delete(`/gestor/despesas/${id}`); toast.success('Removida'); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCreate = () => { setEditing(null); setForm({ ...initialForm, data: new Date().toISOString().split('T')[0] }); setDialogOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ descricao: d.descricao, categoria: d.categoria, valor: String(d.valor), data: d.data?.split('T')[0] || d.data, observacoes: d.observacoes || '' }); setDialogOpen(true); };

  const filtered = despesas.filter(d => d.descricao?.toLowerCase().includes(search.toLowerCase()) || d.categoria?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="despesas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">DESPESAS</h1>
        <Button onClick={openCreate} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-despesa-btn"><Plus className="w-4 h-4 mr-2" /> Nova Despesa</Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input className="pl-10 bg-[#0A0A0A] border-[#27272A]" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Descricao</TableHead>
                <TableHead className="text-zinc-400">Categoria</TableHead>
                <TableHead className="text-zinc-400">Valor</TableHead>
                <TableHead className="text-zinc-400">Data</TableHead>
                <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Nenhuma despesa registrada</TableCell></TableRow>
              ) : filtered.map(d => (
                <TableRow key={d.id} className="border-[#27272A] hover:bg-[#1C1C1E]">
                  <TableCell className="text-white font-medium">{d.descricao}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded-md bg-zinc-700/30 text-zinc-300">{d.categoria}</span></TableCell>
                  <TableCell className="text-red-400">-R$ {d.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-zinc-400">{d.data?.split('T')[0] || d.data}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)} className="text-zinc-400 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">{editing ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Descricao</Label><Input value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="despesa-form-descricao" /></div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Categoria</Label>
              <Select value={form.categoria} onValueChange={v => setForm({...form, categoria: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">{categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor (R$)</Label><Input type="number" step="0.01" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Data</Label><Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Observacoes</Label><Textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Registrar')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
